/**
 * Jertscript XION Standard Library
 *
 * French-themed wrapper functions for the @burnt-labs/abstraxion library.
 * These functions provide a Jertscript-friendly interface for XION blockchain operations.
 *
 * Usage in Jertscript:
 *   asynchrone fonction main() {
 *     croissant portefeuille = attendre connecter();
 *     croissant balance = attendre solde(portefeuille);
 *     dire(balance);
 *   }
 *
 * Security: All inputs are validated before being passed to the blockchain.
 */

// ===========================================================================
// Input Validation Utilities
// ===========================================================================

/**
 * XION address format: xion1... (bech32 encoded, typically 39-45 chars)
 * This regex validates the basic format of XION addresses.
 */
const XION_ADDRESS_REGEX = /^xion1[a-z0-9]{38,58}$/;

/**
 * Validates a XION blockchain address format.
 * @param address - The address to validate
 * @returns True if valid, false otherwise
 */
export function isValidXionAddress(address: string): boolean {
  if (!address || typeof address !== 'string') {
    return false;
  }
  return XION_ADDRESS_REGEX.test(address);
}

/**
 * Validates that an amount is a positive number string.
 * @param amount - The amount to validate
 * @returns True if valid, false otherwise
 */
export function isValidAmount(amount: string): boolean {
  if (!amount || typeof amount !== 'string') {
    return false;
  }
  // Must be a positive integer (no decimals for token amounts in base units)
  const num = parseInt(amount, 10);
  return !isNaN(num) && num > 0 && String(num) === amount;
}

/**
 * Validates a contract message object.
 * @param msg - The message to validate
 * @returns True if valid, false otherwise
 */
export function isValidContractMessage(msg: unknown): msg is Record<string, unknown> {
  if (!msg || typeof msg !== 'object') {
    return false;
  }
  // Contract messages should be plain objects with at least one property
  return Object.keys(msg).length > 0;
}

/**
 * Sanitizes error messages to avoid leaking sensitive blockchain data.
 * @param error - The error to sanitize
 * @returns A sanitized error message
 */
function sanitizeError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  // Remove potentially sensitive data like full transaction hashes, account details
  return message
    .replace(/xion1[a-z0-9]{38,58}/gi, '[ADDRESS]')
    .replace(/[A-Fa-f0-9]{64}/g, '[HASH]')
    .replace(/sequence:\s*\d+/gi, 'sequence: [REDACTED]')
    .substring(0, 200);  // Limit length
}

// Type definitions for abstraxion (peer dependency)
// These mirror the @burnt-labs/abstraxion types

/**
 * Wallet connection result from abstraxion
 */
export interface PortefeuilleXion {
  address: string;
  connected: boolean;
  client?: unknown;
}

/**
 * Token balance information
 */
export interface SoldeInfo {
  denom: string;
  amount: string;
}

/**
 * Transaction result
 */
export interface ResultatTransaction {
  transactionHash: string;
  success: boolean;
  gasUsed?: string;
  logs?: unknown[];
}

/**
 * Contract execution message
 */
export interface MessageContrat {
  contractAddress: string;
  msg: Record<string, unknown>;
  funds?: { denom: string; amount: string }[];
}

/**
 * Contract query message
 */
export interface RequeteContrat {
  contractAddress: string;
  msg: Record<string, unknown>;
}

// Store for the abstraxion client (injected at runtime)
let abstraxionClient: {
  connect?: () => Promise<PortefeuilleXion>;
  disconnect?: () => Promise<void>;
  getBalance?: (address: string, denom?: string) => Promise<SoldeInfo>;
  sendTokens?: (recipient: string, amount: string, denom?: string) => Promise<ResultatTransaction>;
  execute?: (msg: MessageContrat) => Promise<ResultatTransaction>;
  query?: (msg: RequeteContrat) => Promise<unknown>;
} | null = null;

// Current wallet state
let portefeuilleActuel: PortefeuilleXion | null = null;

/**
 * Initialize the XION client (called by runtime)
 * @param client - The abstraxion client instance, or null to reset
 * @throws Error if client is provided but missing required methods
 */
export function initialiserXion(client: typeof abstraxionClient): void {
  // Allow null to reset the client (useful for testing and cleanup)
  if (client === null) {
    abstraxionClient = null;
    portefeuilleActuel = null;
    return;
  }

  // Validate that client has at least one required method
  const requiredMethods = ['connect', 'disconnect', 'getBalance', 'sendTokens', 'execute', 'query'] as const;
  const availableMethods = requiredMethods.filter(method => typeof client[method] === 'function');

  if (availableMethods.length === 0) {
    throw new Error(
      "Attention! Le client fourni n'a aucune methode XION valide. " +
      "Le client doit avoir au moins une des methodes: connect, disconnect, getBalance, sendTokens, execute, query. " +
      "Verifiez que vous utilisez un client @burnt-labs/abstraxion valide."
    );
  }

  abstraxionClient = client;
}

/**
 * connecter() - Connect to XION wallet
 *
 * Jertscript: croissant portefeuille = attendre connecter();
 * JavaScript: const wallet = await connect();
 *
 * @returns Promise<PortefeuilleXion> - The connected wallet info
 * @throws Error with French message if connection fails
 */
export async function connecter(): Promise<PortefeuilleXion> {
  if (!abstraxionClient?.connect) {
    throw new Error(
      "Sacre bleu! XION n'est pas initialise. " +
      "Avez-vous oublie d'importer abstraxion? C'est comme oublier le beurre dans un croissant!"
    );
  }

  try {
    const result = await abstraxionClient.connect();
    portefeuilleActuel = result;

    // Validate the returned address format
    if (result.address && !isValidXionAddress(result.address)) {
      console.warn('Warning: Connected wallet returned an unexpected address format');
    }

    return result;
  } catch (error) {
    throw new Error(
      `Merde! La connexion au portefeuille a echoue: ${sanitizeError(error)}. ` +
      "Votre portefeuille est aussi introuvable que les photos nettes du CTO."
    );
  }
}

/**
 * deconnecter() - Disconnect from XION wallet
 *
 * Jertscript: attendre deconnecter();
 * JavaScript: await disconnect();
 *
 * @returns Promise<void>
 * @throws Error with French message if disconnection fails
 */
export async function deconnecter(): Promise<void> {
  if (!abstraxionClient?.disconnect) {
    throw new Error(
      "Mon ami, vous ne pouvez pas deconnecter ce qui n'est pas connecte. " +
      "C'est comme essayer de quitter une fete ou vous n'etes jamais alle."
    );
  }

  try {
    await abstraxionClient.disconnect();
    portefeuilleActuel = null;
  } catch (error) {
    throw new Error(
      `Zut alors! La deconnexion a echoue: ${sanitizeError(error)}. ` +
      "Votre session est aussi persistante que l'amour du CTO pour les forfaits de ski."
    );
  }
}

/**
 * solde() - Get wallet balance
 *
 * Jertscript: croissant balance = attendre solde(adresse);
 * JavaScript: const balance = await getBalance(address);
 *
 * @param adresse - Optional wallet address (uses connected wallet if not provided)
 * @param denom - Optional token denomination (defaults to uxion)
 * @returns Promise<SoldeInfo> - The balance information
 * @throws Error with French message if query fails
 */
export async function solde(adresse?: string, denom: string = "uxion"): Promise<SoldeInfo> {
  if (!abstraxionClient?.getBalance) {
    throw new Error(
      "Sacre bleu! XION n'est pas initialise. " +
      "Impossible de verifier le solde sans connexion, comme verifier son compte sans Internet."
    );
  }

  const addresseActuelle = adresse || portefeuilleActuel?.address;
  if (!addresseActuelle) {
    throw new Error(
      "Mon ami, quelle adresse voulez-vous consulter? " +
      "Vous devez soit vous connecter avec connecter(), soit fournir une adresse. " +
      "C'est comme demander 'Quel est le prix?' sans specifier le produit."
    );
  }

  // Validate address format if provided explicitly
  if (adresse && !isValidXionAddress(adresse)) {
    throw new Error(
      "Attention! L'adresse fournie n'est pas valide. " +
      "Les adresses XION commencent par 'xion1' et contiennent 39-45 caracteres. " +
      "Verifiez votre adresse - meme le CTO peut copier-coller correctement."
    );
  }

  try {
    return await abstraxionClient.getBalance(addresseActuelle, denom);
  } catch (error) {
    throw new Error(
      `Catastrophe! Impossible d'obtenir le solde: ${sanitizeError(error)}. ` +
      "Votre requete a echoue plus fort que les tentatives de snowboard du CTO."
    );
  }
}

/**
 * envoyer() - Send tokens to a recipient
 *
 * Jertscript: croissant resultat = attendre envoyer(destinataire, montant);
 * JavaScript: const result = await sendTokens(recipient, amount);
 *
 * @param destinataire - The recipient's address
 * @param montant - The amount to send
 * @param denom - Optional token denomination (defaults to uxion)
 * @returns Promise<ResultatTransaction> - The transaction result
 * @throws Error with French message if transfer fails
 */
export async function envoyer(
  destinataire: string,
  montant: string,
  denom: string = "uxion"
): Promise<ResultatTransaction> {
  if (!abstraxionClient?.sendTokens) {
    throw new Error(
      "Sacre bleu! XION n'est pas initialise. " +
      "Impossible d'envoyer des tokens sans connexion. Meme le CTO sait ca."
    );
  }

  if (!portefeuilleActuel?.connected) {
    throw new Error(
      "Attention! Vous devez d'abord vous connecter avec connecter(). " +
      "Envoyer des tokens sans portefeuille, c'est comme faire du ski sans skis."
    );
  }

  if (!destinataire) {
    throw new Error(
      "Mon ami, ou voulez-vous envoyer ces tokens? " +
      "Le destinataire est aussi absent que la mise au point dans les photos du CTO."
    );
  }

  // Validate recipient address format
  if (!isValidXionAddress(destinataire)) {
    throw new Error(
      "Attention! L'adresse du destinataire n'est pas valide. " +
      "Les adresses XION commencent par 'xion1' et contiennent 39-45 caracteres. " +
      "Verifiez l'adresse avant d'envoyer vos precieux tokens!"
    );
  }

  if (!montant || montant === "0") {
    throw new Error(
      "Vraiment? Vous voulez envoyer zero tokens? " +
      "C'est aussi utile que les 47 forfaits de ski du CTO pendant l'ete."
    );
  }

  // Validate amount is a positive integer
  if (!isValidAmount(montant)) {
    throw new Error(
      "Attention! Le montant doit etre un nombre entier positif. " +
      "Les montants negatifs ou decimaux ne sont pas acceptes. " +
      "Les maths, c'est complique, mais pas autant que les photos du CTO."
    );
  }

  try {
    return await abstraxionClient.sendTokens(destinataire, montant, denom);
  } catch (error) {
    throw new Error(
      `Merde! Le transfert a echoue: ${sanitizeError(error)}. ` +
      "Vos tokens sont restes en place, comme le CTO sur une piste noire."
    );
  }
}

/**
 * executer() - Execute a smart contract
 *
 * Jertscript: croissant resultat = attendre executer(message);
 * JavaScript: const result = await execute(msg);
 *
 * @param messageContrat - The contract execution message
 * @returns Promise<ResultatTransaction> - The transaction result
 * @throws Error with French message if execution fails
 */
export async function executer(messageContrat: MessageContrat): Promise<ResultatTransaction> {
  if (!abstraxionClient?.execute) {
    throw new Error(
      "Sacre bleu! XION n'est pas initialise. " +
      "Impossible d'executer un contrat sans connexion. C'est du bon sens, non?"
    );
  }

  if (!portefeuilleActuel?.connected) {
    throw new Error(
      "Attention! Vous devez d'abord vous connecter avec connecter(). " +
      "Executer un contrat sans portefeuille, c'est comme photographier sans appareil."
    );
  }

  if (!messageContrat?.contractAddress) {
    throw new Error(
      "Mon ami, quel contrat voulez-vous executer? " +
      "L'adresse du contrat est requise, comme le pre-workout avant une seance du CTO."
    );
  }

  // Validate contract address format
  if (!isValidXionAddress(messageContrat.contractAddress)) {
    throw new Error(
      "Attention! L'adresse du contrat n'est pas valide. " +
      "Les adresses XION commencent par 'xion1'. Verifiez votre adresse de contrat!"
    );
  }

  // Validate contract message
  if (!isValidContractMessage(messageContrat.msg)) {
    throw new Error(
      "Attention! Le message du contrat est invalide. " +
      "Le message doit etre un objet non-vide. " +
      "Meme le CTO sait qu'on ne peut pas executer un contrat sans instructions!"
    );
  }

  // Validate funds if provided
  if (messageContrat.funds) {
    for (const fund of messageContrat.funds) {
      if (!isValidAmount(fund.amount)) {
        throw new Error(
          "Attention! Un montant de fonds est invalide. " +
          "Les montants doivent etre des entiers positifs."
        );
      }
    }
  }

  try {
    return await abstraxionClient.execute(messageContrat);
  } catch (error) {
    throw new Error(
      `Catastrophe! L'execution du contrat a echoue: ${sanitizeError(error)}. ` +
      "Votre transaction a plante plus fort qu'un debutant sur une double noire."
    );
  }
}

/**
 * requete() - Query a smart contract (read-only)
 *
 * Jertscript: croissant donnees = attendre requete(message);
 * JavaScript: const data = await query(msg);
 *
 * @param messageRequete - The contract query message
 * @returns Promise<unknown> - The query result
 * @throws Error with French message if query fails
 */
export async function requete(messageRequete: RequeteContrat): Promise<unknown> {
  if (!abstraxionClient?.query) {
    throw new Error(
      "Sacre bleu! XION n'est pas initialise. " +
      "Impossible de faire une requete sans connexion. Logique, non?"
    );
  }

  if (!messageRequete?.contractAddress) {
    throw new Error(
      "Mon ami, quel contrat voulez-vous interroger? " +
      "L'adresse du contrat est aussi manquante que le talent photo du CTO."
    );
  }

  // Validate contract address format
  if (!isValidXionAddress(messageRequete.contractAddress)) {
    throw new Error(
      "Attention! L'adresse du contrat n'est pas valide. " +
      "Les adresses XION commencent par 'xion1'. Verifiez votre adresse!"
    );
  }

  // Validate query message
  if (!isValidContractMessage(messageRequete.msg)) {
    throw new Error(
      "Attention! Le message de requete est invalide. " +
      "Le message doit etre un objet non-vide."
    );
  }

  try {
    return await abstraxionClient.query(messageRequete);
  } catch (error) {
    throw new Error(
      `Merde! La requete a echoue: ${sanitizeError(error)}. ` +
      "Le contrat ne repond pas, comme le CTO quand on lui demande de supprimer des photos floues."
    );
  }
}

/**
 * Get the current connected wallet (utility function)
 *
 * @returns The current wallet or null if not connected
 */
export function obtenirPortefeuille(): PortefeuilleXion | null {
  return portefeuilleActuel;
}

/**
 * Check if a wallet is currently connected
 *
 * @returns true if connected, false otherwise
 */
export function estConnecte(): boolean {
  return portefeuilleActuel?.connected ?? false;
}

// Export all XION functions for Jertscript stdlib
export const xionStdlib = {
  connecter,
  deconnecter,
  solde,
  envoyer,
  executer,
  requete,
  obtenirPortefeuille,
  estConnecte,
  initialiserXion,
};

export default xionStdlib;
